import { useEffect, useMemo, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { XIcon } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { useCurrentUser } from '@/features/identity';
import type { WorkspaceTaskSummary } from '@/features/tasks';
import type { TaskView } from '@/lib/api/generated/model';
import { AWAY_MS, sinceSummary, whenWords } from '../sinceSummary';
import { readVisit, writeDismissed, writeLeft } from '../visitStore';

/**
 * Since you were here (spec 155 US1): what happened while the person was
 * away, in one line, from what the page already holds.
 *
 * - The last visit is read once, when the page opens, and kept for the
 *   page's life: another tab leaving cannot erase the news mid-read.
 * - It shows after at least 15 minutes away, when something happened, and
 *   not again after a dismissal until something newer happens.
 * - Leaving the page (hidden, closed or navigated away) records the visit.
 */

const YOUR_TURN_HEADING = 'overview-your-turn';

export function SinceYouWereHere({
  workspaceId,
  attention,
  tasks,
}: {
  workspaceId: string;
  attention: WorkspaceTaskSummary['attention'];
  tasks: readonly TaskView[];
}) {
  const userId = useCurrentUser().data?.id;
  // Read once per person and workspace: the visit as it was when this page
  // opened.
  const visit = useMemo(
    () => (userId ? readVisit(userId, workspaceId) : null),
    [userId, workspaceId],
  );
  const [opened] = useState(() => Date.now());
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!userId) return;
    const leave = () => {
      writeLeft(userId, workspaceId, new Date());
    };
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') leave();
    };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', leave);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', leave);
      leave();
    };
  }, [userId, workspaceId]);

  if (!userId || !visit?.leftAt || dismissed) return null;
  const since = Date.parse(visit.leftAt);
  if (!Number.isFinite(since) || opened - since < AWAY_MS) return null;

  const summary = sinceSummary(attention, tasks, since);
  if (summary.newest === null) return null;
  const dismissedAt = visit.dismissedAt ? Date.parse(visit.dismissedAt) : null;
  if (dismissedAt !== null && summary.newest <= dismissedAt) return null;

  const toYourTurn = () => {
    const heading = document.getElementById(YOUR_TURN_HEADING);
    if (!heading) return;
    heading.tabIndex = -1;
    heading.scrollIntoView?.({ block: 'start' });
    heading.focus();
  };
  const linkClass =
    'text-foreground underline-offset-4 hover:underline focus-visible:ring-ring rounded-sm focus-visible:ring-2 focus-visible:outline-none';

  const parts = [
    summary.runsFinished > 0 ? (
      <Link
        key="runs"
        to="/workspaces/$workspaceId/tasks"
        params={{ workspaceId }}
        className={linkClass}
      >
        {plural(summary.runsFinished, 'run finished', 'runs finished')}
      </Link>
    ) : null,
    summary.decisionsArrived > 0 ? (
      <button
        key="decisions"
        type="button"
        onClick={toYourTurn}
        className={linkClass}
      >
        {plural(
          summary.decisionsArrived,
          'decision arrived',
          'decisions arrived',
        )}
      </button>
    ) : null,
    summary.wentWrong > 0 ? (
      <button
        key="wrong"
        type="button"
        onClick={toYourTurn}
        className={linkClass}
      >
        {`${String(summary.wentWrong)} went wrong`}
      </button>
    ) : null,
  ].filter((part) => part !== null);

  return (
    <section
      aria-label="Since you were here"
      className="border-border bg-card flex items-center gap-3 rounded-lg border px-3.5 py-2 text-xs"
    >
      <span
        aria-hidden="true"
        className="bg-primary size-2 shrink-0 rounded-full"
      />
      <p className="text-muted-foreground min-w-0 flex-1">
        Since you were here {whenWords(since, opened)}:{' '}
        {parts.map((part, index) => (
          <span key={index}>
            {index > 0 ? ' · ' : null}
            {part}
          </span>
        ))}
      </p>
      <Button
        type="button"
        size="icon-xs"
        variant="ghost"
        aria-label="Dismiss what happened since you were here"
        className="pointer-coarse:size-11"
        onClick={() => {
          writeDismissed(userId, workspaceId, new Date());
          setDismissed(true);
        }}
      >
        <XIcon aria-hidden="true" />
      </Button>
    </section>
  );
}

function plural(count: number, one: string, many: string): string {
  return `${String(count)} ${count === 1 ? one : many}`;
}
